import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import Header from "../../components/ui/Header";
import TerminalBootSequence from "./components/TerminalBootSequence";
import ASCIILogo from "./components/ASCIILogo";
import TerminalNavigation from "./components/TerminalNavigation";
import SystemStatusMonitor from "./components/SystemStatusMonitor";
import RotatingTaglines from "./components/RotatingTaglines";
import KonamiCodeHandler from "./components/KonamiCodeHandler";
import Icon from "../../components/AppIcon";
import Footer from "components/ui/Footer";

const TerminalHeroHomepage = () => {
  const [showBootSequence, setShowBootSequence] = useState(() => {
    const bootSession = localStorage.getItem('bootSession');
    if (bootSession) {
      const sessionTime = parseInt(bootSession, 10);
      // If session is less than 5 minutes old, skip boot sequence
      if (Date.now() - sessionTime < 5 * 60 * 1000) {
        return false;
      }
    }
    return true;
  });
  const [isKonamiActivated, setIsKonamiActivated] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleBootComplete = () => {
  setShowBootSequence(false);
  localStorage.setItem('bootSession', Date.now().toString());
  };

  const handleKonamiActivation = () => {
    setIsKonamiActivated(true);
    setTimeout(() => setIsKonamiActivated(false), 1000);
  };

  if (showBootSequence) {
    return <TerminalBootSequence onComplete={handleBootComplete} />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-x-hidden">
      <Helmet>
        <title>DeveloperNoob - Story Of A Noob Developer </title>
        <meta
          name="description"
          content="Backend Developer attempting Frontend - Where bugs become features and CSS is still a mystery"
        />
        <meta
          name="keywords"
          content="developer, backend, frontend, portfolio, terminal, programming"
        />
      </Helmet>

      <Header />

      {/* Scan lines overlay effect */}
      <div className="scan-lines fixed inset-0 pointer-events-none z-10"></div>
      <main className="pt-20 pb-16">
        {/* Hero Section */}
        <section className="relative py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <ASCIILogo />

            <div className="mt-4">
              <RotatingTaglines />
            </div>

            {/* System Info Bar */}
            <div className="mt-8 flex flex-wrap justify-center items-center gap-4 text-xs font-source text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Icon name="Calendar" size={12} />
                <span>{currentTime?.toLocaleDateString()}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Clock" size={12} />
                <span>{currentTime?.toLocaleTimeString()}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="MapPin" size={12} />
                <span>localhost:4044</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                <span>System Online</span>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Access Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-fira text-primary mb-4">
                Shortcut Central
              </h2>
              <p className="text-muted-foreground font-source max-w-2xl mx-auto">
                Because scrolling is overrated
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  title: "Projects Repository",
                  description: "Where todo apps think they’re unicorns",
                  command: "ls projects/",
                  route: "/my-projects",
                  icon: "FolderOpen",
                  color: "text-warning",
                },
                {
                  title: "About Me",
                  description: "Human-readable JSON with extra bugs",
                  command: "cat about.json",
                  route: "/about-me",
                  icon: "User",
                  color: "text-terminal-cyan",
                },
                {
                  title: "Skills Dashboard",
                  description: "CPU 99% on Googling errors",
                  command: "ps aux",
                  route: "/system-monitor-skills-dashboard",
                  icon: "Activity",
                  color: "text-success",
                },
                {
                  title: "Blog Articles",
                  description: "Bug reports disguised as tutorials",
                  command: "tail -f blog.log",
                  route: "/blogs",
                  icon: "FileText",
                  color: "text-terminal-blue",
                },
                {
                  title: "Contact Terminal",
                  description: "Ping me, I might pong back",
                  command: "ping contact",
                  route: "/ping-me",
                  icon: "MessageSquare",
                  color: "text-primary",
                },
                {
                  title: "System Status",
                  description: "Running stable… until someone touches prod",
                  command: "systemctl status",
                  route: "/status",
                  icon: "Monitor",
                  color: "text-destructive",
                },
              ]?.map((item, index) => (
                <div
                  key={index}
                  className="bg-card border border-border rounded-lg p-6 hover:border-primary/30 transition-all duration-200 group terminal-glow"
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <Icon name={item?.icon} size={24} className={item?.color} />
                    <h3 className="text-lg font-fira text-foreground group-hover:text-primary transition-colors duration-200">
                      {item?.title}
                    </h3>
                  </div>

                  <p className="text-muted-foreground text-sm font-source mb-4">
                    {item?.description}
                  </p>

                  <div className="bg-muted/20 border border-border rounded p-2 font-fira text-sm">
                    <span className="text-terminal-cyan">$ </span>
                    <span className="text-foreground">{item?.command}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Terminal Navigation Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-fira text-primary mb-4">
                Navigation Terminal
              </h2>
              <p className="text-muted-foreground font-source max-w-2xl mx-auto">
                Type commands below to explore.. Don’t worry, nothing will break
                — but feel free to pretend you’re hacking
              </p>
            </div>

            <TerminalNavigation />
          </div>
        </section>

        {/* System Status Monitor Section */}
        {/* <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/5">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-fira text-primary mb-4">
                Developer Metrics Dashboard
              </h2>
              <p className="text-muted-foreground font-source max-w-2xl mx-auto">
                Live surveillance of my questionable coding skills and current
                brain glitches. Stats auto-update every time I break something
                and Google saves me.
              </p>
            </div>

            <SystemStatusMonitor />
          </div>
        </section> */}

        {/* Easter Egg Hints */}
        <section className="py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-card border border-border rounded-lg p-6 terminal-glow">
              <div className="flex items-center justify-center space-x-2 mb-3">
                <Icon name="Gamepad2" size={16} className="text-warning" />
                <span className="text-sm font-fira text-warning">
                  Easter Egg Alert
                </span>
              </div>
              <p className="text-muted-foreground text-xs font-source">
                Try typing some creative commands in the terminal above, or use
                your keyboard for special sequences...
              </p>
              <div className="mt-2 text-xs text-muted-foreground/60 font-source">
                Hint: ↑↑↓↓←→←→BA might unlock something special
              </div>
            </div>
          </div>
        </section>
      </main>
      {/* Footer */}
      <Footer />

      {/* Konami Code Handler */}
      <KonamiCodeHandler onActivate={handleKonamiActivation} />
    </div>
  );
};

export default TerminalHeroHomepage;
